import { Box, Button, TextField, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { subscribeToMailingList } from '../utils/api';

interface EmailSubscribeBoxProps {
  formType: string; // e.g. "contest", "newsletter", "footer"
  title?: string;
  subtitle?: string;
  onSuccess?: () => void;
}

const EmailSubscribeBox: React.FC<EmailSubscribeBoxProps> = ({
  formType,
  title = 'Stay Updated',
  subtitle = 'No spam, unsubscribe anytime. Updates only.',
  onSuccess,
}) => {
  const theme = useTheme();
  const [email, setEmail] = useState<string>('');

  const handleSubmit = async () => {
    try {
      const res = await subscribeToMailingList(email, formType);

      if (!res) {
        console.error('Error subscribing to mailing list');
        return;
      }
      setEmail('');
      toast.success('We will keep you updated!');
      if (onSuccess) onSuccess();
    } catch (error: any) {
      toast.error(error.message || 'Something went wrong. Try again later.');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        gap: 2,
        ...(title
          ? {
              bgcolor: theme.palette.background['container-2'],
              py: 4,
              px: 4,
              borderRadius: 3,
            }
          : {}),
      }}
    >
      {title && (
        <Typography
          variant='h6'
          color={theme.palette.primary.main}
          align='center'
        >
          {title}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          gap: 1,
        }}
      >
        <TextField
          fullWidth
          placeholder='Enter your email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          slotProps={{
            input: {
              sx: {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? theme.palette.background.paper
                    : 'common.white',
                color: 'text.primary',
                borderRadius: 2,
                '& fieldset': {
                  border: '2px solid #42424250',
                },
              },
            },
          }}
          variant='outlined'
        />
        <Button
          variant='contained'
          size='large'
          sx={{
            bgcolor: theme.palette.primary.main,
            width: { xs: 1, md: 'auto' },
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 600,
            '&:hover': {
              bgcolor: theme.palette.primary.dark,
            },
          }}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Box>
      <Typography variant='body1' color='text.secondary' align='center'>
        {subtitle}
      </Typography>
    </Box>
  );
};

export default EmailSubscribeBox;